import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Rating from '@mui/material/Rating';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import useSnackbar from '../hooks/useSnackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { usePostMutation } from '../hooks/useAuthenticatedMutation';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2)
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1)
    }
}));

const StyledRating = styled(Rating)(({ theme }) => ({
    '& .MuiRating-iconEmpty .MuiSvgIcon-root': {
        color: theme.palette.action.disabled
    }
}));

const customIcons = {
    1: { icon: <SentimentVeryDissatisfiedIcon color="error" sx={{ fontSize: 50 }} />, label: 'Very Dissatisfied' },
    2: { icon: <SentimentVeryDissatisfiedIcon color="error" sx={{ fontSize: 50 }} />, label: 'Very Dissatisfied' },
    3: { icon: <SentimentDissatisfiedIcon color="error" sx={{ fontSize: 50 }} />, label: 'Dissatisfied' },
    4: { icon: <SentimentDissatisfiedIcon color="error" sx={{ fontSize: 50 }} />, label: 'Dissatisfied' },
    5: { icon: <SentimentDissatisfiedIcon color="error" sx={{ fontSize: 50 }} />, label: 'Dissatisfied' },
    6: { icon: <SentimentDissatisfiedIcon color="error" sx={{ fontSize: 50 }} />, label: 'Dissatisfied' },
    7: { icon: <SentimentDissatisfiedIcon color="error" sx={{ fontSize: 50 }} />, label: 'Dissatisfied' },
    8: { icon: <SentimentSatisfiedIcon color="warning" sx={{ fontSize: 50 }} />, label: 'Satisfied' },
    9: { icon: <SentimentSatisfiedIcon color="warning" sx={{ fontSize: 50 }} />, label: 'Satisfied' },
    10: { icon: <SentimentVerySatisfiedIcon color="success" sx={{ fontSize: 50 }} />, label: 'Very Satisfied' },
    11: { icon: <SentimentVerySatisfiedIcon color="success" sx={{ fontSize: 50 }} />, label: 'Very Satisfied' }
};

const IconContainer = ({ value, ...other }) => {
    return <span {...other}>{customIcons[value]?.icon}</span>;
};

const NpsDialog = ({ open, onClose }) => {
    const npsSurveyMutation = usePostMutation('user/nps-survey', 'user');
    const [rating, setRating] = useState(null);
    const [feedback, setFeedback] = useState('');
    const { showSnackbar } = useSnackbar();
    const [submitted, setSubmitted] = useState(false);

    const handleRatingChange = (event, newValue) => {
        setRating(newValue);
        setFeedback('');
    };

    const handleSubmit = () => {
        if (rating - 1 < 7 && feedback.trim() === '') {
            showSnackbar('Please provide feedback on how we can improve.', 'error');
            return;
        }

        const onSuccess = () => {
            setSubmitted(true);
            showSnackbar('Customer Satisfaction Survey score submitted successfully', 'success');
            onClose();
        };

        npsSurveyMutation.mutate({ score: rating - 1, response: feedback }, { onSuccess });
    };

    const getConditionalText = () => {
        if (rating > 9) {
            return 'Wow, thank you! What are we doing to earn such a great score? Also, what new feature would you like to see?';
        }
        else if (rating >= 8 && rating <= 9) {
            return 'Thank you, that\'s a great score. What features are missing that would increase that score by one?';
        }
        else if (rating < 8) {
            return 'We\'re not hitting the mark. What features would you like to see, or what can we do to improve?';
        }

        return null;
    };

    return (
        <StyledDialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2 }}>Quarterly Customer Satisfaction Survey</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500]
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers={rating !== null}>
                {submitted ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            my: 3
                        }}
                    >
                        <Typography variant="h6" gutterBottom>Thank you for your feedback!</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Your responses and feedback are a vital part of our omni-channel platform and service. Please click below to see the improvements and upgrades we have made based on your feedback.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            href="https://newzapp.co.uk/why-newzapp/releases-bug-fixes-and-improvements/"
                            target="_blank"
                        >
                            View Upgrades and Releases
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Typography variant="body1" gutterBottom>
                            Just 2 clicks for you that means the world to us!
                        </Typography>
                        <Typography variant="body1">
                            How likely are you to recommend NewZapp to a friend or colleague?
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 2 }}>
                            <StyledRating
                                name="nps-rating"
                                value={rating}
                                onChange={handleRatingChange}
                                max={11}
                                size="large"
                                slotProps={{
                                    icon: { component: IconContainer }
                                }}
                                highlightSelectedOnly
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                                {Array.from({ length: 11 }, (_, i) => (
                                    <Typography
                                        key={i + 1}
                                        variant="caption"
                                        sx={{ width: 42, textAlign: 'center' }}
                                    >
                                        {i}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>

                        {rating !== null && (
                            <>
                                <Typography variant="body1" gutterBottom>
                                    {getConditionalText()}
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    variant="outlined"
                                    required={rating !== null && rating - 1 < 7}
                                    error={rating !== null && rating - 1 < 7 && feedback.trim() === ''}
                                    helperText={
                                        rating !== null && rating - 1 < 7
                                            ? 'Required field'
                                            : undefined
                                    }
                                    slotProps={{
                                        input: {
                                            maxLength: 800
                                        }
                                    }}
                                />
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {submitted ? (
                    <Button onClick={onClose} variant="contained" color="primary">
                        Close
                    </Button>
                ) : (
                    rating && (
                        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={rating === null || (rating - 1 < 7 && feedback.trim() === '')}>
                            Submit
                        </Button>
                    )
                )}
            </DialogActions>
        </StyledDialog>
    );
};

export default NpsDialog;
